/**
 * AppShell — top-level chrome shared by every authenticated route.
 *
 * One responsive, mobile-first shell. There is no separate desktop layout — a
 * native mobile app is the target, so desktop is the SAME app, widened and
 * centered, never a different design. The mobile chrome renders at every width:
 *
 *   header (brand · Planning/Shopping switcher · sync · ☰ account)
 *     →  main (centered, comfortable max-width column)
 *     →  BottomNav (List · Deals · Feedback)  +  CaptureFab (＋)
 *
 * The ☰ account button is the account/products entry at every width — it opens
 * the AccountSheet (catalog + app settings + sign out). There is no separate
 * desktop user menu or top tab nav.
 *
 * Layover screens (products / settings) hide the switcher, bottom nav, and FAB
 * so those focused config surfaces read as exits, not mode destinations — at
 * every width. Settings keeps its "‹ Back" affordance.
 *
 * Wide screens: the app is constrained to a centered column (≈600px, matching
 * the prototype's desktop plan column) so the content + chrome read as the
 * mobile app made wider, not stretched edge-to-edge. The bottom nav stays fixed
 * but is centered/aligned to that column rather than spanning a wide monitor.
 * Inline styles can't carry media queries, so a single injected <style> sets
 * the column width + centers the fixed bottom nav. Safe-area-aware, ≥44px
 * targets throughout.
 *
 * GPS mode default (T12.6): the mode is purely location-derived. Auto-detection
 * runs on entry (native: directly; web: silently when already granted) via
 * `useModeDetection`; the header switcher is a temporary in-memory deviation; and
 * the logo is the mid-session reset — pressing it recomputes the mode from GPS
 * (`requestDetection`) and still navigates to /home. The persistent
 * LocationNeededBanner is the ambient consumer when location isn't working AND
 * the household has no home coordinates (T12.6.1 home-coords gate) — this shell
 * is the SOLE `useModeDetection` caller, and prop-drills `locationWorking` /
 * `hasHomeCoords` / `requestDetection` into both the banner and ModeSpine (whose
 * own post-manual-switch hint uses the same three values).
 * All styles flow from tokens (CP5). No hex, no raw px literals.
 */

import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { routes } from '../routes/paths';
import { useModeDetection } from '../stores/useModeDetection';
import { useAppMode } from '../stores/useAppMode';
import { AccountSheet } from './AccountSheet';
import { ModeSpine } from './ModeSpine';
import { BottomNav } from './BottomNav';
import { CaptureFab } from './CaptureFab';
import { LocationNeededBanner } from './LocationNeededBanner';
import { ReportSheet } from '../features/report-problems';
import { ConfirmEmailNudge } from '../features/auth';
import { Logo, SyncStatusIndicator, Icon } from '../components';
import { useSyncEngine } from '../lib/useSyncEngine';
import { useAndroidBackButton } from '../lib/useAndroidBackButton';
import { recordLastSyncedNow } from '../stores/deviceSettings';
import { useLayoverTitle } from '../stores/useLayoverTitle';

// The app reads as a phone app at every width: a comfortable centered column.
// `--appshell-column` is the shared width for the main content and the fixed
// bottom nav so the nav never becomes an edge-to-edge bar on a wide monitor.
// Inline styles can't express `left: 50%; transform: translateX(-50%)` against
// a token width cleanly across components, so the column + the bottom-nav
// centering live in this one injected stylesheet. The under-nav reserve lives
// on `mainStyle` inline (a class `padding-bottom` here would lose to the inline
// `padding` shorthand), so this class only carries the centered-column geometry.
const RESPONSIVE_CSS = `
.appshell-main {
  width: 100%;
  max-width: var(--appshell-column);
  margin-left: auto;
  margin-right: auto;
}
.appshell-bottom-nav {
  /* The nav's base inline style sets left:0; right:0 (full-bleed on phone).
     Inline styles beat a class selector, so override with !important to center
     the nav to the app column on wider screens. */
  left: 50% !important;
  right: auto !important;
  transform: translateX(-50%);
  width: 100%;
  max-width: var(--appshell-column);
}
`;

export interface AppShellProps {
  children: ReactNode;
}

// The centered app-column width — the prototype's desktop plan column feel.
const APPSHELL_COLUMN = '40rem';

const shellStyle: CSSProperties = {
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: 'var(--color-bg-primary)',
  color: 'var(--color-text-primary)',
  fontFamily: 'var(--font-family-sans)',
  // Expose the shared column width to the injected stylesheet below.
  ['--appshell-column' as string]: APPSHELL_COLUMN,
};

// Exported for AppShell.test.tsx — happy-dom's CSSOM silently drops nested
// `env()` / `max()` / `calc()` values when React assigns them via `el.style`,
// so a DOM-read assertion can't see the authored safe-area expression. The
// test asserts on this source object instead (the value React renders).
export const headerStyle: CSSProperties = {
  // The shell is a `minHeight:100vh` flex column and `<main>` has no overflow,
  // so the window/body is the scroll container. `position: sticky; top: 0` pins
  // the header to the viewport top while the content scrolls beneath it. The
  // `--color-bg-primary` background below is fully solid (no alpha), so scrolling
  // content stays hidden behind the header rather than bleeding through. The
  // `--z-sticky` layer (100) sits above the scrolling content but below the
  // overlay layer `--z-modal` (1000), so the capture / account sheets still
  // cover the header when open. (Cast pattern mirrors fabStyle/overlayStyle.)
  position: 'sticky',
  top: 0,
  zIndex: 'var(--z-sticky)' as unknown as number,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: 'var(--space-3) var(--space-6)',
  // T27.2.5.1 (A2) — pad the top clear of the device status bar. On an
  // edge-to-edge Android WebView the header otherwise draws under the
  // clock/battery. `max(...)` keeps the normal `--space-3` top padding when
  // there is no inset (web/desktop), and grows to the real status-bar height
  // when the native safe-area plugin (T27.2.2) populates the inset. The
  // shorthand `padding` above still sets the side + bottom padding; this
  // longhand overrides only the top (a later longhand wins over the earlier
  // shorthand in the same style object).
  paddingTop: 'max(var(--space-3), env(safe-area-inset-top, 0px))',
  borderBottomWidth: '0.0625rem',
  borderBottomStyle: 'solid',
  borderBottomColor: 'var(--color-border-subtle)',
  // Chrome stays at page level, not card-elevated. In dark, cards lift a step
  // lighter than the page while the header reads as chrome at the page surface
  // (the proto relationship). In light, primary==raised==white so this is a
  // no-op.
  backgroundColor: 'var(--color-bg-primary)',
};

const headerLeftStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
};

const headerLogoLinkStyle: CSSProperties = {
  textDecoration: 'none',
  color: 'inherit',
  display: 'inline-flex',
  alignItems: 'center',
};

// Header center title — shown in the slot the ModeSpine vacates on surfaces that
// hide the switcher but still want a page label in the header (the Feedback tab,
// a user-directed deviation from the proto). It occupies the same flex slack the
// spine did (flex:1) so it centers between the brand dot and the ☰ control.
const headerTitleStyle: CSSProperties = {
  flex: 1,
  margin: '0 var(--space-2)',
  textAlign: 'center',
  // T47 Phase 4 — in-app header page title renders the Poppins display face.
  fontFamily: 'var(--font-family-display)',
  fontSize: 'var(--font-size-md)',
  fontWeight: 'var(--font-weight-semibold)',
  color: 'var(--color-text-primary)',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const headerRightStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-2)',
  flexShrink: 0,
};

// ☰ account trigger — the account/products entry at every width. Opens the
// AccountSheet that holds the catalog + app settings + sign out (the surfaces
// dropped from the bottom nav). Uses the rounded-rect token (`--radius-md`), not
// a pill. Exported for AppShell.test.tsx (the radius assertion).
export const accountButtonStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: '2.75rem',
  minHeight: '2.75rem',
  fontSize: 'var(--font-size-md)',
  backgroundColor: 'transparent',
  color: 'var(--color-text-secondary)',
  borderWidth: '0.0625rem',
  borderStyle: 'solid',
  borderColor: 'var(--color-border-strong)',
  borderRadius: 'var(--radius-md)',
  cursor: 'pointer',
};

// Layover bottom scrim — the masking the fixed bottom nav gives normal screens,
// recreated for layover surfaces (products / settings) that hide the nav. On a
// normal screen the solid `--color-bg-primary` bottom nav (with its own
// safe-area padding) covers the device's bottom safe-area band, so scrolling
// content never shows under the Android system-nav buttons / home indicator. A
// layover hides the nav (`{!isLayover && <BottomNav/>}`), leaving that band
// uncovered — so MID-scroll, page content passing through the
// `env(safe-area-inset-bottom)` band is visible under the translucent system
// buttons (the `main` bottom reserve only clears the LAST element at max scroll).
// This fixed strip masks exactly that band with the PAGE surface
// (`--color-bg-primary`) so the band blends with the page (no dark strip). The
// Android OS nav buttons (◀ ● ■) stay visible over it because their icon
// appearance now FOLLOWS the app theme — dark icons in light mode, light in dark
// — driven natively from the theme switch (apps/web/src/adapters/systemUi.ts →
// the embedded NavigationBar plugin). Its height IS the safe-area inset, so on
// web/desktop (inset 0) the strip collapses to 0 and is a visual no-op. It sits
// at `--z-sticky` (the nav's layer) — above scrolling content, below the
// `--z-modal` overlay layer so sheets still cover it. Exported for
// AppShell.test.tsx (same happy-dom CSSOM limitation as the safe-area values
// below — happy-dom drops nested env()/calc() on a DOM read).
// BUG51 — the shared ambient-banner stacking container. ConfirmEmailNudge and
// LocationNeededBanner used to EACH hardcode the identical `position:fixed` +
// `bottom` + `z-index` (see the pre-fix `nudgeStyle` / `bannerStyle` in those
// files); when both were eligible at once (a fresh signup: unverified email +
// no location) nothing distinguished them — one painted over the other and
// swallowed its clicks (a `confirm-email-dismiss` click never landed because
// the LocationNeededBanner, mounted after it, painted on top at the identical
// rect). This ONE container now owns the fixed positioning + z-index; the two
// banners render as plain flex-row children of it and stack vertically (gap)
// when both are eligible, instead of overlapping. A single child (the common
// case) sits at exactly the same rect it always did — solo appearance is
// unchanged. Exported for AppShell.test.tsx (happy-dom drops nested
// calc()/env() on a DOM read, same limitation as headerStyle above).
export const ambientBannerStackStyle: CSSProperties = {
  position: 'fixed',
  left: 'var(--space-3)',
  right: 'var(--space-3)',
  // Same offset both banners used individually before (lifted above the
  // CaptureFab's top edge, 8rem + safe-area inset).
  bottom: 'calc(8rem + env(safe-area-inset-bottom, 0px) + var(--space-2))',
  zIndex: 'var(--z-banner)' as unknown as number,
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-2)',
};

export const layoverBottomScrimStyle: CSSProperties = {
  position: 'fixed',
  left: 0,
  right: 0,
  bottom: 0,
  height: 'env(safe-area-inset-bottom, 0px)',
  // The page surface, so the masked band blends with the page (no dark strip).
  // The OS nav icons stay legible because they follow the app theme natively
  // (systemUi.ts), so a dark masking token is no longer needed.
  backgroundColor: 'var(--color-bg-primary)',
  zIndex: 'var(--z-sticky)' as unknown as number,
  // Purely a background mask; never intercept touches meant for content above.
  pointerEvents: 'none',
};

// Exported for AppShell.test.tsx (same happy-dom CSSOM limitation as
// headerStyle above — the bottom reserve is a `calc(... + env(...))` value).
export const mainStyle: CSSProperties = {
  flex: 1,
  // Top + sides from the scale. The bottom reserve is set explicitly (not via
  // the `padding` shorthand) so it clears the FIXED bottom nav (~56px) + the
  // device safe-area at every width. The shorthand previously emitted
  // `padding-bottom: 16px` inline, which beat the `.appshell-main`
  // `calc(5rem + safe-area)` class rule (inline wins over a class) — so the
  // last element of a short surface (e.g. the Shopping "Show purchased" toggle)
  // sat under the nav. Setting the reserve inline makes it actually apply.
  paddingTop: 'var(--space-4)',
  paddingLeft: 'var(--space-6)',
  paddingRight: 'var(--space-6)',
  // T27.2.5.1 (H3) — clear the ＋ CaptureFab, not just the bottom nav. The FAB
  // (CaptureFab.tsx fabStyle) sits at `bottom: 4.5rem + safe-area` and is
  // 3.5rem tall, so its TOP edge reaches 8rem above the safe-area baseline.
  // The old 5rem reserve cleared only the ~3.5rem nav, so a trailing
  // actionable row (e.g. Deals "View deal", the Home "Suggested for you" line)
  // in the bottom-right could land under the FAB and be unreachable. 9rem
  // clears the FAB's 8rem top edge by 1rem (`--space-4`); + the safe-area
  // inset so the last row never sits under the FAB, the nav, or the home
  // indicator.
  paddingBottom: 'calc(9rem + env(safe-area-inset-bottom, 0px))',
  boxSizing: 'border-box',
};

export function AppShell({ children }: AppShellProps): JSX.Element {
  const location = useLocation();

  // The header is `position: sticky; top: 0` with a VARIABLE height: the top
  // padding grows with `env(safe-area-inset-top)` on a native edge-to-edge
  // WebView (and on rotation), so there is no fixed-height token a second sticky
  // element could offset against. Measure the live header height and publish it
  // as `--app-header-height` (in px) on the shell root, so a sticky element
  // inside `<main>` (e.g. the Feedback "Propose a feature" bar) can pin just
  // BELOW the header instead of sliding under it.
  //
  // The FIXED bottom nav has the same problem in the other direction: its real
  // height on device = its base row + `env(safe-area-inset-bottom)`, taller than
  // any fixed offset token. Measure its LIVE height too and publish it as
  // `--app-bottomnav-height` (in px) so a pinned element above it
  // (DonationBanner) clears the nav regardless of the device inset. The nav is
  // `fixed` with safe-area padding, so its `offsetHeight` already includes the
  // device inset. One ResizeObserver tracks both elements — the safe-area inset
  // populating, rotation, and width changes; cleaned up on unmount. The nav is
  // absent on layover surfaces, so the effect re-runs when `isLayover` flips to
  // (re)observe the nav once it mounts.
  const shellRef = useRef<HTMLDivElement | null>(null);
  const headerRef = useRef<HTMLElement | null>(null);
  const bottomNavRef = useRef<HTMLElement | null>(null);

  // Drives the GPS mode soft-default. AppShell is the SOLE caller of the hook —
  // its one-shot entry effect runs auto-detection (native: `getCurrentPosition`
  // directly, raising the OS prompt on first launch; web: silently only when
  // already granted). The outcome + home-coords presence + trigger flow into the
  // ambient LocationNeededBanner below (shows while location isn't working AND
  // there's no home-coords fallback, T12.6.1) and into ModeSpine (its own
  // post-manual-switch hint).
  const { locationWorking, hasHomeCoords, requestDetection } = useModeDetection();
  const mode = useAppMode();
  const { pullState, sseState } = useSyncEngine();
  const [accountOpen, setAccountOpen] = useState(false);

  // T27.2.7.3 — the single app-level Android hardware-Back handler. Native-only
  // (no-op on web); closes the topmost open overlay before navigating back, so
  // Back dismisses the deal sheet / account menu / capture sheet / settings modal
  // instead of popping the route underneath it (DL1/DL2/MN2).
  useAndroidBackButton();

  // T12.2 step 10b — record a real "last synced" timestamp whenever a catch-up
  // pull completes (pull transitions out of 'pulling' into 'idle'). The
  // App-settings Offline status row reads it so it shows a genuine time rather
  // than a fake "Just now". Device-local; no backend channel.
  const wasPulling = useRef(false);
  useEffect(() => {
    if (pullState === 'pulling') {
      wasPulling.current = true;
    } else if (wasPulling.current && pullState === 'idle') {
      wasPulling.current = false;
      recordLastSyncedNow();
    }
  }, [pullState]);

  // Layover surfaces: products + settings are focused, set-once config screens,
  // not mode destinations — so the mode spine, bottom nav, and FAB step aside
  // there. Settings additionally swaps the spine for a Back link.
  const isSettings = location.pathname.startsWith(routes.settings);
  const isProducts = location.pathname.startsWith(routes.products);
  const isLayover = isSettings || isProducts;

  // Feedback + Deals are real nav destinations (the bottom nav STAYS, unlike a
  // layover), but the mode switcher is suppressed on BOTH (T39): Feedback is
  // independent of App mode, and Deals' store awareness comes from the
  // session LocationContext, not the operational mode — so the switcher drives
  // nothing on either and is hidden there (each shows a plain header title
  // instead). The ＋ FAB is hidden on both too (app-planning.html:2666): it is
  // the "add to list" action; on Deals you add via the deal sheet and on
  // Feedback there is no list, so the floating ＋ would be a dead control.
  const isFeedback = location.pathname.startsWith(routes.feedback);
  const isDeals = location.pathname.startsWith(routes.deals);

  // The header-center page title, shown where the switcher is hidden but a label
  // still belongs in the header. Feedback shows `Feedback`; Deals shows `Deals`
  // (T39 — the mode switcher no longer rides on Deals, so the route gets a plain
  // title in the same slot). The derived form keeps adding such surfaces on one
  // line.
  const headerTitle = isFeedback ? 'Feedback' : isDeals ? 'Deals' : null;

  // Layover surfaces (products / settings) hide the ModeSpine AND the derived
  // `headerTitle` (both gated `!isLayover`), so the header center is empty there.
  // Fill it with the CURRENT layover page's own name: each layover page publishes
  // its title (the same string it shows in-content) via `usePublishLayoverTitle`,
  // and we read it here — so the header label always matches the page, including
  // dynamic sub-page titles (e.g. a member's name) and the products layover. Fall
  // back to "Settings" for the brief render before the page publishes (or if a
  // layover route fails to publish) so the slot is never empty. Mutually
  // exclusive with the spine + derived title (rendered only when `isLayover`).
  const publishedLayoverTitle = useLayoverTitle();
  const layoverTitle = isLayover
    ? (publishedLayoverTitle ?? 'Settings')
    : null;

  // Publish the live header + bottom-nav heights (see the refs above). Re-runs
  // when `isLayover` flips because the nav unmounts on layover surfaces, so the
  // observer must re-attach to the fresh node when it returns.
  useEffect(() => {
    const header = headerRef.current;
    const shell = shellRef.current;
    if (!header || !shell) return;
    const apply = (): void => {
      shell.style.setProperty('--app-header-height', `${header.offsetHeight}px`);
      const nav = bottomNavRef.current;
      if (nav) {
        shell.style.setProperty('--app-bottomnav-height', `${nav.offsetHeight}px`);
      }
    };
    apply();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(apply);
    ro.observe(header);
    if (bottomNavRef.current) ro.observe(bottomNavRef.current);
    return () => ro.disconnect();
  }, [isLayover]);

  // Soft-keyboard handling on the native Android WebView is done at the NATIVE
  // layer by @capacitor/keyboard (`resize: 'native'`, see
  // apps/mobile/capacitor.config.ts), which resizes the WebView itself when the
  // IME opens so `window.innerHeight` shrinks to the visible height and the
  // layout viewport == the visible area. With a truly-resized layout viewport,
  // the sticky header, the Feedback "Propose a feature" bar, and the fixed
  // bottom nav all pin correctly via plain CSS — no per-element JS. The earlier
  // `visualViewport`-translate header hack was removed here: once the WebView
  // resizes, the header sticks natively and the translate would double-offset
  // it. (The browser/PWA path honors the viewport-meta
  // `interactive-widget=resizes-content` in index.html, where plain sticky
  // already works.)

  return (
    <div style={shellStyle} data-testid="app-shell" ref={shellRef}>
      <style>{RESPONSIVE_CSS}</style>

      <header style={headerStyle} data-testid="app-header" ref={headerRef}>
        <div style={headerLeftStyle}>
          <NavLink
            to={routes.home}
            aria-label="Shopista — go to home"
            data-testid="app-title"
            style={headerLogoLinkStyle}
            // T12.6 Reopen 2 — the logo is the mid-session mode reset: press it
            // to recompute the mode from the current location. No preventDefault
            // — it still navigates to /home. When the fix is "elsewhere" or
            // unavailable, detectMode returns null and the mode is left unchanged.
            onClick={() => requestDetection()}
          >
            {/* Mark-only brand dot — the wordmark is dropped so the header fits
                the brand · switcher · controls row on a single line at 360px
                (matches the prototype's compact brand dot). The full wordmark
                still appears on the login hero. */}
            <Logo size={32} data-testid="app-logo" />
          </NavLink>
        </div>

        {/* Header center slot. The Planning/Shopping switcher lives here in the
            header row (prototype), between the brand and the controls — hidden on
            layover surfaces (products / settings) and on the Feedback tab (the
            proto hides `#seg` on s-feedback). Where the switcher is hidden but a
            page label belongs in the header, a header title fills the vacated
            slot — currently the Feedback tab (user-directed deviation from the
            proto: the title moves into the header instead of an h1 on the page).
            `headerTitle` is derived, so adding more titled-but-spineless surfaces
            later is a one-line change. T39: the switcher is hidden on Deals too
            (it no longer drives any Deals behavior), so Deals shows its title in
            the vacated slot. */}
        {!isLayover && !isFeedback && !isDeals && (
          <ModeSpine
            locationWorking={locationWorking}
            hasHomeCoords={hasHomeCoords}
            requestDetection={requestDetection}
          />
        )}
        {!isLayover && headerTitle && (
          <h1 style={headerTitleStyle} data-testid="app-header-title">
            {headerTitle}
          </h1>
        )}
        {/* Layover surfaces (products / settings) show the current page's own
            name in the slot the spine/derived title vacate (published by the page
            via usePublishLayoverTitle; falls back to "Settings" before publish).
            Reuses the same style + testid as the Feedback/Deals title so it reads
            identically. Mutually exclusive with the two above. */}
        {layoverTitle && (
          <h1 style={headerTitleStyle} data-testid="app-header-title">
            {layoverTitle}
          </h1>
        )}

        <div style={headerRightStyle}>
          <SyncStatusIndicator pullState={pullState} sseState={sseState} />
          {/* ☰ opens the account sheet (catalog + settings + sign out) — the
              account entry at every width. */}
          <button
            type="button"
            style={accountButtonStyle}
            onClick={() => setAccountOpen(true)}
            aria-haspopup="menu"
            aria-expanded={accountOpen}
            aria-label="Account and products"
            data-testid="app-account-trigger"
          >
            <Icon name="menu" data-testid="app-account-trigger-icon" />
          </button>
        </div>
      </header>

      <main style={mainStyle} className="appshell-main">
        {children}
      </main>

      {/* Thumb-zone chrome — renders at every width, hidden on layover surfaces.
          The bottom nav is centered to the app column (see RESPONSIVE_CSS). */}
      {!isLayover && <BottomNav ref={bottomNavRef} />}

      {/* Layover bottom scrim — masks the device safe-area band on surfaces that
          hide the bottom nav (products / settings), so scrolling content never
          shows under the Android system-nav buttons (the nav does this masking
          on normal screens). Collapses to 0 height where there is no inset
          (web/desktop), so it's a no-op there. */}
      {isLayover && (
        <div style={layoverBottomScrimStyle} data-testid="layover-bottom-scrim" />
      )}

      {/* Capture FAB + sheet — the floating ＋ and the picker sheet render at
          every width; hidden on layover surfaces AND on the Deals + Feedback
          tabs (the proto hides the FAB on s-deals || s-feedback). */}
      {!isLayover && !isFeedback && !isDeals && <CaptureFab />}

      {/* T48 / T12.6 D6 (home-coords gate T12.6.1), BUG51 — the non-blocking
          confirm-email nudge and the persistent GPS-needed banner both mount
          into this ONE ambient-banner stack (ambientBannerStackStyle above)
          instead of each carrying its own fixed position. Each still self-gates
          (renders null until its own conditions hold), so an empty stack (both
          null) has no content and intercepts nothing. Pinned above the bottom
          nav on the real nav surfaces; hidden on the focused layover config
          screens. When both are eligible at once they stack (flex column, gap)
          rather than occupying the identical rect. */}
      {!isLayover && (
        <div style={ambientBannerStackStyle} data-testid="ambient-banner-stack">
          <ConfirmEmailNudge />
          <LocationNeededBanner
            locationWorking={locationWorking}
            hasHomeCoords={hasHomeCoords}
            requestDetection={requestDetection}
          />
        </div>
      )}

      <AccountSheet open={accountOpen} onClose={() => setAccountOpen(false)} />

      {/* T35.3 / F21 — the shared report sheet, mounted once. Ambient by
          construction: it renders NOTHING until the user opens it on intent (☰
          entry, the Feedback card, the all-done shopping row), so mounting it in
          every mode never interrupts, blocks, or nags — there is no auto-open,
          no timer, no passive prompt. The GUARDRAILS rule is about the affordance
          never *appearing on its own* in executing mode; a user-initiated open
          (incl. the all-done in-store touch-point) is the user's choice and is
          allowed. `data-app-mode` records the mode for the E2E guard. */}
      <div data-testid="report-sheet-host" data-app-mode={mode}>
        <ReportSheet />
      </div>
    </div>
  );
}
